
import axios from "axios";
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { roundNumber } from "../../../../utils/inputsValidation";
import api from "../../../../utils/api";
import moment from "moment";

// 🔹 Fund Categories
const LABEL_ORDER = [
  "STP Funds",
  "Conservative Funds",
  "Moderate Risk Funds",
  "Aggressive Funds",
  "Very Aggressive Funds",
];

const COLOR_MAP = {
  "STP Funds": "#cfcf0e",
  "Conservative Funds": "#26b99a",
  "Moderate Risk Funds": "#3498db",
  "Aggressive Funds": "#2bd927",
  "Very Aggressive Funds": "#267e24",
};

// 🔹 Year Categories
const FINAL_LABEL_ORDER = [
  "1-3 Years",
  "3-5 Year",
  "Above 5 Years",
  "Below 1 Year",
  "Temporary Parking",
];

const FINAL_COLOR_MAP = {
  "1-3 Years": "#cfcf0e",
  "3-5 Year": "#26b99a",
  "Above 5 Years": "#3498db",
  "Below 1 Year": "#2bd927",
  "Temporary Parking": "#267e24",
};

// helper to build chart parts
function toChartParts(graphObj = {}, labelsOrder, colorMap) {
  const labels = labelsOrder.filter((lbl) => graphObj[lbl] !== undefined);
  const series = labels.map((lbl) => Number(graphObj[lbl] || 0));
  const colors = labels.map((lbl) => colorMap[lbl] || "#999999");
  return { labels, series, colors };
}

const DonutChart = ({ title, percentage, labels, series, colors }) => {
  const options = {
    chart: { type: "donut" },
    labels,
    colors,
    plotOptions: {
      pie: {
        donut: {
          size: "80%",
          labels: {
            show: true,
            total: {
              show: false,
              label: title,
              formatter: () => `${percentage}%`,
              color: "#000",
              fontSize: "20px",
            },
            value: {
              show: true,
              fontSize: "20px",
              formatter: (val) => `${val}%`,
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: {
      theme: "dark",
      y: { formatter: (val) => `${val}%` },
    },
    responsive: [
      {
        breakpoint: 992,
        options: { chart: { width: 300 } },
      },
    ],
  };

  return <Chart options={options} series={series} type="donut" width="400" />;
};

export const TabThree = ({
  setSelectedTab,
  selectedTab,
  roomId,
  isPortfolioData,
  isPortfolioDataNEW,
  isChecked,
  setIsChecked,
}) => {
  const handleToggle = () => setIsChecked((prev) => !prev);

  const [isResponseData, setIsResponseData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isPortfolioData?.data?.prinfo?.portfolio_review_id||isPortfolioDataNEW?.data?.prinfo?.portfolio_review_id) {
      getPrData();
    }
  }, [isPortfolioData,isPortfolioDataNEW, isChecked]);

  const getPrData = async () => {
    try {
      setLoading(true);
      const payload = {
        clientid: roomId,
        pr_id: isPortfolioData?.data?.prinfo?.portfolio_review_id||isPortfolioDataNEW?.data?.prinfo?.portfolio_review_id,
        step: 53,
        action: "whyReblance",
        usertype: "CLI",
        toggle_: isChecked ? 0 : 1, // ✅ Toggle logic
      };

      const new_pr_date = isPortfolioData?.data?.prinfo?.new_pr_date||isPortfolioDataNEW?.data?.prinfo?.new_pr_date; 
       const cutoffDate = moment("2025-09-25");       // static date jis se compare karna hai
      
                // String ko moment me convert karna
                const formattedPrDate = moment(new_pr_date);
      
                let endpoint = "";
                if (formattedPrDate.isBefore(cutoffDate)) {
                  endpoint = "advisor/updateprdata";
                } else {
                  endpoint = "advisor/updateprdataWM";
                }
      

      const response = await api.post(endpoint, payload);
      setIsResponseData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center my-5">Loading data...</div>;
  }

  // ✅ build charts based on toggle
  const nowParts = isChecked
    ? toChartParts(isResponseData?.nowgraph || {}, LABEL_ORDER, COLOR_MAP)
    : toChartParts(isResponseData?.nowgraph || {}, FINAL_LABEL_ORDER, FINAL_COLOR_MAP);

  const interimParts = isChecked
    ? toChartParts(isResponseData?.interimgraph || {}, LABEL_ORDER, COLOR_MAP)
    : toChartParts(isResponseData?.interimgraph || {}, FINAL_LABEL_ORDER, FINAL_COLOR_MAP);

  const finalParts = isChecked
    ? toChartParts(isResponseData?.finalgraph || {}, LABEL_ORDER, COLOR_MAP)
    : toChartParts(isResponseData?.finalgraph || {}, FINAL_LABEL_ORDER, FINAL_COLOR_MAP);

  const chartsData = [
    {
      title: "Now",
      percentage: 99,
      title2: isResponseData?.gamount?.now ?? 0,
      ...nowParts,
    },
    {
      title: "Interim",
      percentage: 90,
      title2: isResponseData?.gamount?.interim ?? 0,
      ...interimParts,
    },
    {
      title: "Final",
      percentage: 100,
      title2: isResponseData?.gamount?.final ?? 0,
      ...finalParts,
    },
  ];

  // ✅ legends also switch
  const legendItems = isChecked
    ? LABEL_ORDER.map((label) => ({ label, color: COLOR_MAP[label] }))
    : FINAL_LABEL_ORDER.map((label) => ({ label, color: FINAL_COLOR_MAP[label] }));


  const handleNext = () => {
    if (selectedTab < 4) setSelectedTab(selectedTab + 1);
  };
  const handleBack = () => {
    if (selectedTab > 1) setSelectedTab(selectedTab - 1);
  };

  return (
    <>
      <div className="container" style={{ minHeight: "500px", paddingBottom: "20px" }}>
        <div className="d-flex justify-content-between align-items-center">
          <h2>What changes will we bring about?</h2>
          <span className="form-check form-switch toggle-color">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={isChecked}
              onChange={handleToggle}
            />
          </span>
        </div>

        {/* Charts Section */}
        <div className="row justify-content-center text-center">
          {chartsData.map((data, index) => (
            <div key={index} className="col-md-4 col-12 mb-4 d-flex flex-column align-items-center">
              <p className="fw-bold">
                {data.title} <span className="fw-normal">(₹ {roundNumber(data.title2)})</span>
              </p>
              <DonutChart
                title={data.title}
                percentage={data.percentage}
                labels={data.labels}
                series={data.series}
                colors={data.colors}
              />
            </div>
          ))}
        </div>

        {/* Legend Section */}
        <div className="d-flex flex-wrap justify-content-center mt-4">
          {legendItems.map((item, index) => (
            <div className="d-flex align-items-center me-4 mb-2" key={index}>
              <span
                style={{
                  backgroundColor: item.color,
                  width: "16px",
                  height: "16px",
                  borderRadius: "4px",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              />
              <span className="verify">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="container">
        <div className="row">
          <div className="col text-end">
            <a href="javascript:void(0);" onClick={handleBack} aria-disabled={selectedTab === 1}>
              <img className="arrow_next1" src="/assets/images/left-arrowpng.png" alt="Back" />
            </a>
            <a
              href="javascript:void(0);"
              className="ms-1"
              onClick={handleNext}
              aria-disabled={selectedTab === 4}
            >
              <img className="arrow_next" src="/assets/images/right-arrow.svg" alt="Next" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
